import React, { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useQuery, useMutation } from '@apollo/client'
import { decodeGlobalId } from '~/utils/id'
import visit from '~/utils/visit'
import { useRouter } from 'next/router'
import qs from 'querystringify'
import { useViewer } from '~/components/ViewerContext'
import ADD_TICKETS_TO_CART from '~/graphql/mutations/addTicketsToCart'
import GET_VIEWERS_ACTIVE_TICKET_ALERTS from '~/graphql/queries/getViewersActiveTicketAlerts'
import {
  Avatar,
  fontSize,
  space,
  device,
  Button,
  Text,
  H2,
  color,
  radius,
  fontWeight,
  Alert,
} from '@ticketswap/solar'
import { InfoRounded } from '@ticketswap/comets'
import PriceInformationDialog from '../PriceInformationDialog'
import { getLinkProps, track } from '../../utils'
import useLocalStorage from '~/hooks/useLocalStorage'
import AuthDialog from '../AuthDialog'
import { AUTHENTICATION_VIEWED, PRODUCT_ADDED } from '~/lib/tracking-events'
import Money, { formatMoney } from '~/utils/money'
import { useLocale } from '~/components/LocaleContext'
import QuantitySelector from '~/components/Purchaser/QuantitySelector'
import SeatSelection from '~/components/SeatSelection'
import Alerts from '~/components/Purchaser/Alerts'
import NoAvailableTickets from '~/components/Purchaser/NoAvailableTickets'

const AvatarContainer = styled.div`
  margin-right: ${space[16]};
`

const Comment = styled.div`
  background-color: ${color.stardust};
  border-radius: ${radius.lg};
  padding: ${space[16]};
  display: flex;

  strong {
    display: block;
    font-weight: ${fontWeight.bold};
  }
`

const Container = styled.div`
  margin-bottom: ${space[8]};

  @media ${device.tablet} {
    margin-bottom: ${space[16]};
  }
`

const PurchaserContainer = styled.div`
  background-color: ${color.nova};
  border: 1px solid ${color.spaceLightest};
  border-radius: ${radius.lg};
  padding: ${space[16]};

  @media ${device.tablet} {
    padding: ${space[32]} ${space[24]};
  }
`

const Body = styled.div`
  & > * + * {
    margin-top: ${space[16]};
  }
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  position: relative;

  @media ${device.tablet} {
    flex-direction: ${props => (props.flex ? 'column' : 'row')};
  }
`

const Price = styled.div`
  height: ${space[44]};
  display: flex;
  align-items: center;
  margin-top: 0;
  position: ${props => (props.withSeating ? 'relative' : 'absolute')};
  width: ${props => (props.withSeating ? '100%' : '50%')};
  justify-content: ${props => (props.withSeating ? 'center' : 'flex-end')};
  top: 0;
  right: 0;

  @media ${device.tablet} {
    position: relative;
    align-self: initial;
    width: ${props => (props.withSeating ? '100%' : 'calc(100% / 3)')};
    align-items: flex-start;
    justify-content: center;
    height: auto;
    margin-top: 0;
    /* Optical adjustment */
    transform: translateY(${props => (props.withSeating ? 1 : -1)}px);
  }
`

const InfoButton = styled.button`
  color: ${color.spaceLighter};
  margin-left: ${space[8]};
  transform: translateY(-2px);

  @media ${device.tablet} {
    transform: translateY(4px);
  }
`

const BuyButtonContainer = styled.div`
  width: 100%;
  margin-top: ${space[16]};

  button {
    width: 100%;
  }

  @media ${device.tablet} {
    margin-top: ${props => (props.withSeating ? space[16] : 0)};
    text-align: right;
    width: ${props => (props.withSeating ? '100%' : 'calc(100% / 3)')};

    button {
      width: ${props => (props.withSeating ? '100%' : 'auto')};
    }
  }
`

const OriginalPrice = styled.p`
  font-size: ${fontSize[14]};
  text-align: center;
  color: ${color.spaceLight};
  margin-top: ${space[8]};

  @media ${device.tablet} {
    position: ${props => (props.withSeating ? 'relative' : 'absolute')};
    bottom: 0;
    left: 50%;
    transform: translateX(-50%) translateY(1px);
  }
`

const Purchaser = ({ t, listing, status, isBuyingBlocked, ...props }) => {
  const locale = useLocale()
  const router = useRouter()
  const query = qs.parse(router.asPath.split('?')[1]) || {}
  const { addToCart: addToCartQueryString = 1 } = query
  const { viewer: currentViewer } = useViewer()
  const [, setRecentlyVisitedListing] = useLocalStorage('recently_visited_listing', null)
  const { id, event = {}, eventType = {}, price = {} } = listing
  const isBundledTickets = Boolean(eventType && eventType.bundledTickets)
  const bundledTicketsAmount = isBundledTickets ? eventType.bundledTickets.amount : 0
  const mutated = useRef(false)
  const submitButtonRef = useRef()
  const [quantity, setQuantity] = useState(
    isBundledTickets ? bundledTicketsAmount : addToCartQueryString
  )

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [canSubmit, setCanSubmit] = useState(currentViewer)
  const initialTicketSelection = listing?.tickets?.edges
    .filter(({ node }) => node.status === 'AVAILABLE')
    .map(({ node }) => node.id)
  const [ticketSelection, setTicketSelection] = useState(
    query && query.addTicketsToCart ? query.addTicketsToCart.split(',') : initialTicketSelection
  )
  const numberOfTicketsFromListingInUsersCart = props.ticketsFromListingInUsersCart.length
  const availableTickets = listing?.tickets?.edges.filter(({ node }) => node.status === 'AVAILABLE')
  const numberOfAvailableTickets = availableTickets && availableTickets.length
  const showScarcityAlert =
    listing.numberOfTicketsInListing > 1 &&
    numberOfAvailableTickets !== listing.numberOfTicketsInListing
  const { seatingOptions = [] } = eventType || {}
  const eventTypeHasSeating =
    Object.keys(seatingOptions).filter(key => seatingOptions[key] === true).length > 0
  const {
    sellerPrice,
    serviceFeeForBuyer,
    transactionFeePerTicket,
    totalPriceWithTransactionFee,
  } = price
  const { href: cartHref } = getLinkProps('CART')
  const { data = {} } = useQuery(GET_VIEWERS_ACTIVE_TICKET_ALERTS, {
    skip: !canSubmit,
  })
  const { viewer } = data
  const isSoldDeletedOrReserved =
    status.listingIsSold || status.listingIsDeleted || status.listingIsReserved
  const showEditOptions = Boolean(viewer?.id === listing?.seller?.id && !isSoldDeletedOrReserved)
  const ticketAlertsForCurrentListing = alert =>
    alert?.event?.id === listing?.event?.id || alert?.eventType?.id === listing?.eventType?.id
  const userHasActiveTicketAlertsForCurrentListing = viewer
    ? viewer.activeTicketAlerts.filter(ticketAlertsForCurrentListing).length > 0
    : query && query.reference === 'wanted'
  const [addToCart] = useMutation(ADD_TICKETS_TO_CART)
  const ticketAmount = eventTypeHasSeating ? ticketSelection.length : quantity

  useEffect(() => {
    // React to `?addToCart` and `?addTicketsToCart` URL params
    if (mutated.current === false && (query?.addToCart || query?.addTicketsToCart)) {
      submitButtonRef.current && submitButtonRef.current.click()
    }
  }, [mutated, query])

  async function handleSubmit(e) {
    e.preventDefault()
    if (loading) return false
    setError(null)
    setLoading(true)
    mutated.current = true

    const {
      data: { addTicketsToCart },
    } = await addToCart({
      variables: {
        input: {
          listingId: listing.id,
          listingHash: listing.hash,
          ...(!eventTypeHasSeating && {
            amountOfTickets: quantity,
          }),
          ...(eventTypeHasSeating && {
            ticketIds: ticketSelection,
          }),
        },
      },
    })

    if (addTicketsToCart?.errors.length > 0) {
      setLoading(false)
      return setError(addTicketsToCart.errors[0])
    }

    track(PRODUCT_ADDED, {
      product_id: decodeGlobalId(eventType ? eventType.id : event.id),
      name: eventType?.title,
      brand: event.name,
      price: new Money(totalPriceWithTransactionFee),
      quantity,
      variant: decodeGlobalId(id),
    })

    // Store this listing in localStorage so
    // we can link back to it after clicking
    // the `Exit`-button of the checkout.
    setRecentlyVisitedListing({
      slug: listing.event.slug,
      id: decodeGlobalId(listing.id),
      hash: listing.hash,
    })

    // Redirect
    visit({ href: cartHref })
  }

  if (status.listingHasAvailableTickets && isBuyingBlocked)
    return (
      <Container>
        <Alert
          variant="warning"
          action={{
            label: t('common:generic.moreInfo'),
            onClick: () => {
              visit(getLinkProps('HELP_ITEM', { id: 137 }))
            },
          }}
        >
          {t('event-eventtype:status.buyingBlocked.text')}
        </Alert>
      </Container>
    )

  return (
    <Container>
      {!status.listingHasAvailableTickets && (
        <NoAvailableTickets
          t={t}
          status={status}
          isSoldDeletedOrReserved={isSoldDeletedOrReserved}
          userHasActiveTicketAlertsForCurrentListing={userHasActiveTicketAlertsForCurrentListing}
          currentViewer={currentViewer}
          eventName={event.name}
        />
      )}

      {status.listingHasAvailableTickets && (
        <PurchaserContainer>
          <Body>
            <Form onSubmit={handleSubmit} flex={eventTypeHasSeating}>
              {eventTypeHasSeating ? (
                <SeatSelection
                  t={t}
                  listing={listing}
                  initialSelection={initialTicketSelection}
                  onChange={selection => setTicketSelection(selection)}
                />
              ) : (
                <QuantitySelector
                  t={t}
                  numberOfTicketsInListing={listing.numberOfTicketsInListing}
                  isBundledTickets={isBundledTickets}
                  numberOfTicketsStillForSale={listing.numberOfTicketsStillForSale}
                  setQuantity={setQuantity}
                  quantity={quantity}
                  numberOfAvailableTickets={numberOfAvailableTickets}
                  showScarcityAlert={showScarcityAlert}
                />
              )}

              <Price withSeating={eventTypeHasSeating}>
                <H2 as="span" data-testid="total-price">
                  {new Money(totalPriceWithTransactionFee, locale).multiply(ticketAmount).format()}
                </H2>
                <PriceInformationDialog
                  t={t}
                  ticketQuantity={parseInt(ticketAmount)}
                  ticketDescription={`${listing?.event?.name} – ${listing?.eventType?.title}`}
                  priceWithoutFees={new Money(sellerPrice, locale).multiply(ticketAmount).format()}
                  serviceFee={new Money(serviceFeeForBuyer, locale).multiply(ticketAmount).format()}
                  transactionFee={new Money(transactionFeePerTicket, locale)
                    .multiply(ticketAmount)
                    .format()}
                  totalPrice={new Money(totalPriceWithTransactionFee, locale)
                    .multiply(ticketAmount)
                    .format()}
                  withExplanation={true}
                >
                  {({ show }) => (
                    <InfoButton data-testid="price-info-toggle" type="button" onClick={show}>
                      <InfoRounded size={24} />
                    </InfoButton>
                  )}
                </PriceInformationDialog>
              </Price>

              <BuyButtonContainer withSeating={eventTypeHasSeating}>
                <AuthDialog
                  onToggle={on => on && track(AUTHENTICATION_VIEWED)}
                  onComplete={() => {
                    // Bit hairy this implementation. Think of an improvement.
                    setCanSubmit(true)
                    submitButtonRef.current.click()
                  }}
                  emailLoginRedirectUrl={`${global.location && global.location.href}${
                    eventTypeHasSeating
                      ? `?addTicketsToCart=${ticketSelection.join(',')}`
                      : `?addToCart=${quantity}`
                  }`}
                >
                  {({ show }) => (
                    <Button
                      ref={submitButtonRef}
                      loading={loading}
                      disabled={
                        (eventTypeHasSeating && ticketSelection.length === 0) || showEditOptions
                      }
                      type={canSubmit ? 'submit' : 'button'}
                      fullWidth
                      onClick={canSubmit ? () => {} : show}
                    >
                      {(!eventTypeHasSeating && quantity > 1) ||
                      (eventTypeHasSeating && ticketSelection.length > 1)
                        ? t('listing:purchaser.buyTicket_plural')
                        : t('listing:purchaser.buyTicket')}
                    </Button>
                  )}
                </AuthDialog>
              </BuyButtonContainer>

              <OriginalPrice withSeating={eventTypeHasSeating}>
                {t('listing:purchaser.originalPrice', {
                  amount: formatMoney(listing?.price?.originalPrice),
                })}
              </OriginalPrice>
            </Form>

            {listing.description && (
              <Comment>
                <AvatarContainer>
                  <Avatar src={listing.seller.avatarBig} size={52} />
                </AvatarContainer>
                <Text>
                  <strong>
                    {t('listing:purchaser.commentFrom', {
                      name: listing.seller.firstname,
                    })}
                  </strong>
                  {listing.description}
                </Text>
              </Comment>
            )}

            <Alerts
              t={t}
              isBundledTickets={isBundledTickets}
              bundledTicketsAmount={bundledTicketsAmount}
              error={error}
              event={event}
              query={query}
              totalPriceWithTransactionFee={totalPriceWithTransactionFee}
              locale={locale}
              listing={listing}
              numberOfTicketsFromListingInUsersCart={numberOfTicketsFromListingInUsersCart}
              showEditOptions={showEditOptions}
              cartHref={cartHref}
            />
          </Body>
        </PurchaserContainer>
      )}
    </Container>
  )
}

Purchaser.propTypes = {
  t: PropTypes.func.isRequired,
  listing: PropTypes.object.isRequired,
  status: PropTypes.object.isRequired,
  ticketsFromListingInUsersCart: PropTypes.array,
}

export default Purchaser
